import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useRoutes } from 'react-router-dom';

import Box from '@mui/material/Box';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';

import { varAlpha } from 'src/theme/styles';
import { AuthLayout } from 'src/layouts/auth';
import { DashboardLayout } from 'src/layouts/dashboard';

export const LandingPage = lazy(() => import('src/pages/landing'));
export const DashboardPage = lazy(() => import('src/pages/dashboard'));
export const AppsPage = lazy(() => import('src/pages/projects'));
export const AppConfigPage = lazy(() => import('src/pages/config'));
export const AppSettingsPage = lazy(() => import('src/pages/project-settings'));
export const UserPage = lazy(() => import('src/pages/user'));
export const OrganisationsPage = lazy(() => import('src/pages/organization'));
export const ProfilePage = lazy(() => import('src/pages/profile'));
export const ContentManagementPage = lazy(() => import('src/pages/content-management/index'));
export const ContentManagementCreatePage = lazy(() => import('src/pages/content-management/create'));
export const ContentManagementDetailPage = lazy(() => import('src/pages/content-management/detail'));
export const ContentViewEditPage = lazy(() => import('src/pages/content-management/edit'));
export const AppAudiencesPage = lazy(() => import('src/pages/audience'));
export const SignInPage = lazy(() => import('src/pages/sign-in'));
export const SignUpPage = lazy(() => import('src/pages/sign-up'));
export const ForgotPasswordPage = lazy(() => import('src/pages/forgot-password'));
export const EmailVerificationPage = lazy(() => import('src/pages/email-verification'));
export const PricingPage = lazy(() => import('src/pages/pricing'));
export const Page404 = lazy(() => import('src/pages/page-not-found'));
export const CampaignsPage = lazy(() => import('src/pages/campaigns'));
export const CampaignCreatePage = lazy(() => import('src/pages/campaigns/create/index'));
export const CampaignDetailPage = lazy(() => import('src/pages/campaigns/detail'));
export const CampaignEditPage = lazy(() => import('src/pages/campaigns/edit'));
export const AppEventsPage = lazy(() => import('src/pages/project-events'));
export const AppCustomersPage = lazy(() => import('src/pages/app-customer'));
export const AppCustomerDetailPage = lazy(() => import('src/pages/app-customer/detail'));
export const WebhooksPage = lazy(() => import('src/pages/webhooks'));
export const CreateWebhookPage = lazy(() => import('src/pages/webhooks/create'));
export const WebhookDetailPage = lazy(() => import('src/pages/webhooks/detail'));
export const EditWebhookPage = lazy(() => import('src/pages/webhooks/edit'));

const renderFallback = (
  <Box display="flex" alignItems="center" justifyContent="center" flex="1 1 auto">
    <LinearProgress
      sx={{
        width: 1,
        maxWidth: 320,
        bgcolor: (theme) => varAlpha(theme.vars.palette.text.primaryChannel, 0.16),
        [`& .${linearProgressClasses.bar}`]: { bgcolor: 'text.primary' },
      }}
    />
  </Box>
);

export function Router() {
  return useRoutes([
    {
      path: '/',
      index: true,
      element: <LandingPage />,
    },
    {
      path: 'dashboard',
      element: (
        <DashboardLayout>
          <Suspense fallback={renderFallback}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      ),
      children: [
        { element: <DashboardPage />, index: true },
        { path: 'projects', element: <AppsPage /> },
        { path: 'projects/:id/content', element: <ContentManagementPage /> },
        { path: 'projects/:id/content/create', element: <ContentManagementCreatePage /> },
        { path: 'projects/:id/content/:contentViewId', element: <ContentManagementDetailPage /> },
        { path: 'projects/:id/content/:contentViewId/edit', element: <ContentViewEditPage /> },
        { path: 'projects/:id/campaigns', element: <CampaignsPage /> },
        { path: 'projects/:id/campaigns/create', element: <CampaignCreatePage /> },
        { path: 'projects/:id/campaigns/:campaignId', element: <CampaignDetailPage /> },
        { path: 'projects/:id/campaigns/:campaignId/edit', element: <CampaignEditPage /> },
        { path: 'projects/:id/events', element: <AppEventsPage /> },
        { path: 'projects/:id/audiences', element: <AppAudiencesPage /> },
        { path: 'projects/:id/users', element: <AppCustomersPage /> },
        { path: 'projects/:id/users/:userId', element: <AppCustomerDetailPage /> },
        { path: 'projects/:id/webhooks', element: <WebhooksPage /> },
        { path: 'projects/:id/webhooks/create', element: <CreateWebhookPage /> },
        { path: 'projects/:id/webhooks/:webhookId', element: <WebhookDetailPage /> },
        { path: 'projects/:id/webhooks/:webhookId/edit', element: <EditWebhookPage /> },
        { path: 'projects/:id/config', element: <AppConfigPage /> },
        { path: 'projects/:id/collaborators', element: <UserPage /> },
        { path: 'projects/:id/app-settings', element: <AppSettingsPage /> },
        { path: 'organizations', element: <OrganisationsPage /> },
        { path: 'profile', element: <ProfilePage /> },
        { path: 'user', element: <UserPage /> },
      ],
    },
    {
      path: 'sign-in',
      element: (
        <AuthLayout>
          <SignInPage />
        </AuthLayout>
      ),
    },
    {
      path: 'sign-up',
      element: (
        <AuthLayout>
          <SignUpPage />
        </AuthLayout>
      ),
    },
    {
      path: 'forgot-password',
      element: (
        <AuthLayout>
          <ForgotPasswordPage />
        </AuthLayout>
      ),
    },
    { path: 'verify-email', element: <EmailVerificationPage /> },
    {
      path: 'pricing',
      element: <PricingPage />,
    },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
}
